/**
 * /tools/energy-audit?clientId=X — STP-033 · **Workday Audit** (Part 1 of
 * "Workday Audit & Masterpiece Day").
 *
 * The client walks through a *typical* day as a timeline of activities and
 * scores each on Energy / Work / Love (1..10, axis icons). A live summary
 * surfaces the day's E→W→L balance + energy peaks/drains. The audited
 * timeline pre-fills the Masterpiece Day (Part 2).
 *
 * Display name is "Workday Audit"; the key/route stay `energy-audit`.
 * Homework-accessible before Step 4 (server HOMEWORK_TOOL_KEYS).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { ApiError } from '@/lib/api-client';
import { FinalizeWarning } from '@/components/finalize-warning';
import { PrintButton } from '@/components/print-button';
import { AxisIcon } from '@/components/axis-icon';

const AXES = ['energy', 'work', 'love'] as const;
type Axis = (typeof AXES)[number];

interface ActivityForm {
  startTime: string;
  endTime: string;
  label: string;
  energy: number;
  work: number;
  love: number;
}

interface FormState {
  isWeekend: boolean;
  isRegularDay: boolean;
  activities: ActivityForm[];
}

const SEED_TIMES: [string, string][] = [
  ['06:30', '08:00'],
  ['08:00', '12:00'],
  ['12:00', '13:00'],
  ['13:00', '17:00'],
  ['17:00', '20:00'],
  ['20:00', '22:00'],
];

const DEFAULT_FORM: FormState = {
  isWeekend: false,
  isRegularDay: false,
  activities: SEED_TIMES.map(([startTime, endTime]) => ({
    startTime,
    endTime,
    label: '',
    energy: 5,
    work: 5,
    love: 5,
  })),
};

/** "HH:MM" + minutes → "HH:MM", clamped to 23:59. */
function addMinutes(hhmm: string, mins: number): string {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (!m) return hhmm;
  const total = Math.min(23 * 60 + 59, Number(m[1]) * 60 + Number(m[2]) + mins);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

function buildPayload(form: FormState): Record<string, unknown> {
  const activities = form.activities
    .filter((a) => a.label.trim().length > 0 && a.startTime)
    .map((a) => ({
      startTime: a.startTime,
      ...(a.endTime ? { endTime: a.endTime } : {}),
      label: a.label.trim(),
      energy: a.energy,
      work: a.work,
      love: a.love,
    }));
  return {
    isWeekend: form.isWeekend,
    isRegularDay: form.isRegularDay,
    activities,
  };
}

export function EnergyAuditPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const clientId = useClientId();

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  // STP-033 add-line — focus the newly added activity's End field so the
  // client can immediately adjust the auto-filled +30-min duration.
  const endRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [focusEndIdx, setFocusEndIdx] = useState<number | null>(null);
  useEffect(() => {
    if (focusEndIdx === null) return;
    endRefs.current[focusEndIdx]?.focus();
    setFocusEndIdx(null);
  }, [focusEndIdx]);

  useEffect(() => {
    let active = true;
    if (!clientId) {
      setHydrated(true);
      return;
    }
    void (async () => {
      try {
        const existing = await api.getToolCompletion(accessToken, clientId, 'energy-audit');
        if (!active) return;
        if (existing?.payload && typeof existing.payload === 'object') {
          const p = existing.payload as Record<string, unknown>;
          const next: FormState = { ...DEFAULT_FORM };
          if (typeof p.isWeekend === 'boolean') next.isWeekend = p.isWeekend;
          if (typeof p.isRegularDay === 'boolean') next.isRegularDay = p.isRegularDay;
          if (Array.isArray(p.activities) && p.activities.length > 0) {
            next.activities = p.activities.map((raw) => {
              const a = raw as Record<string, unknown>;
              return {
                startTime: typeof a.startTime === 'string' ? a.startTime : '',
                endTime: typeof a.endTime === 'string' ? a.endTime : '',
                label: typeof a.label === 'string' ? a.label : '',
                energy: typeof a.energy === 'number' ? a.energy : 5,
                work: typeof a.work === 'number' ? a.work : 5,
                love: typeof a.love === 'number' ? a.love : 5,
              };
            });
          }
          setForm(next);
          setSavedAt(existing.completed_at ?? null);
          setSavedSnapshot(JSON.stringify(buildPayload(next)));
        }
      } catch {
        /* swallow */
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId]);

  const filled = useMemo(
    () => form.activities.filter((a) => a.label.trim().length > 0 && a.startTime),
    [form.activities],
  );

  const summary = useMemo(() => {
    if (filled.length === 0) return null;
    const avg = (k: Axis): number =>
      Math.round((filled.reduce((s, a) => s + a[k], 0) / filled.length) * 10) / 10;
    // Where each domain peaks — the activity that gives the most Energy / Work
    // engagement / Love connection. These are the hooks for the Masterpiece Day.
    const peakFor = (k: Axis): ActivityForm =>
      filled.reduce((best, a) => (a[k] > best[k] ? a : best), filled[0]!);
    const drain = filled.reduce((worst, a) => (a.energy < worst.energy ? a : worst), filled[0]!);
    return {
      energy: avg('energy'),
      work: avg('work'),
      love: avg('love'),
      peaks: { energy: peakFor('energy'), work: peakFor('work'), love: peakFor('love') } as Record<
        Axis,
        ActivityForm
      >,
      drain,
    };
  }, [filled]);

  const currentSnapshot = useMemo(() => JSON.stringify(buildPayload(form)), [form]);
  const isSavedClean = savedAt !== null && savedSnapshot === currentSnapshot;
  const canSubmit = !submitting && hydrated && filled.length >= 1;

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);
    if (!clientId) {
      setSubmitError(t('tools.energyAudit.errors.missingClient'));
      return;
    }
    setSubmitting(true);
    const payload = buildPayload(form);
    try {
      const completion = await api.submitToolCompletion(accessToken, clientId, 'energy-audit', {
        payload,
      });
      setSavedAt(completion.completed_at ?? new Date().toISOString());
      setSavedSnapshot(JSON.stringify(payload));
    } catch (cause) {
      if (cause instanceof ApiError) {
        setSubmitError(cause.envelope.message || t('tools.energyAudit.errors.generic'));
      } else {
        setSubmitError(t('tools.energyAudit.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section className="max-w-2xl mx-auto px-s5 py-s8" data-testid="energy-audit-missing-client">
        <h1 className="font-display text-display-lg text-ink">{t('tools.energyAudit.title')}</h1>
        <p className="mt-s3 text-flame-ember">{t('tools.energyAudit.errors.missingClient')}</p>
      </section>
    );
  }

  const setActivity = (idx: number, key: keyof ActivityForm, value: string | number): void => {
    setForm((prev) => ({
      ...prev,
      activities: prev.activities.map((a, i) => (i === idx ? { ...a, [key]: value } : a)),
    }));
  };
  const addActivity = (): void => {
    setForm((prev) => {
      if (prev.activities.length >= 30) return prev;
      // Continue the day from where the last activity ended: new start = the
      // previous end (fallback its start), new end = +30 min. The client then
      // tweaks the end time, focus already landed there.
      const last = prev.activities[prev.activities.length - 1];
      const start =
        [last?.endTime, last?.startTime].find(
          (c): c is string => !!c && /^\d{2}:\d{2}$/.test(c),
        ) ?? '06:00';
      return {
        ...prev,
        activities: [
          ...prev.activities,
          { startTime: start, endTime: addMinutes(start, 30), label: '', energy: 5, work: 5, love: 5 },
        ],
      };
    });
    setFocusEndIdx(form.activities.length < 30 ? form.activities.length : null);
  };
  const removeActivity = (idx: number): void => {
    setForm((prev) =>
      prev.activities.length <= 1
        ? prev
        : { ...prev, activities: prev.activities.filter((_, i) => i !== idx) },
    );
  };

  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7">
      <div className="flex items-center justify-between mb-s2">
        <Link
          to={`/tools/masterpiece-blueprint?clientId=${encodeURIComponent(clientId)}`}
          className="text-sm text-flame-deep hover:text-flame"
          data-testid="energy-audit-back"
        >
          {t('tools.energyAudit.backToTool')}
        </Link>
        <PrintButton />
      </div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.energyAudit.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {t('tools.energyAudit.title')}
        </h1>
        <p className="mt-s3 text-gray-2 max-w-prose">{t('tools.energyAudit.description')}</p>
      </header>

      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        className="space-y-s5"
        noValidate
      >
        {/* Day type */}
        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <span className="form-label">{t('tools.energyAudit.dayType.title')}</span>
          <div className="mt-s2 flex flex-wrap gap-s4">
            <YesNoToggle
              label={t('tools.energyAudit.dayType.weekend')}
              value={form.isWeekend}
              onChange={(v) => setForm((prev) => ({ ...prev, isWeekend: v }))}
              testId="energy-audit-weekend"
              yesLabel={t('tools.energyAudit.dayType.yes')}
              noLabel={t('tools.energyAudit.dayType.no')}
            />
            <YesNoToggle
              label={t('tools.energyAudit.dayType.regular')}
              value={form.isRegularDay}
              onChange={(v) => setForm((prev) => ({ ...prev, isRegularDay: v }))}
              testId="energy-audit-regular"
              yesLabel={t('tools.energyAudit.dayType.yes')}
              noLabel={t('tools.energyAudit.dayType.no')}
            />
          </div>
        </article>

        {/* Timeline */}
        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <h2 className="font-display text-display-sm text-ink">
            {t('tools.energyAudit.timeline.title')}
          </h2>
          <p className="mt-s2 text-gray-2 italic">{t('tools.energyAudit.timeline.hint')}</p>
          {/* Left rail = a continuous timeline spine so the day reads as one
              flow; each activity is a light block (no heavy border) so the
              scores no longer chop the timeline into separate cards. */}
          <ol className="mt-s4 space-y-s2 border-l-2 border-bone-3 pl-s4">
            {form.activities.map((a, idx) => (
              <li
                key={idx}
                className="rounded-md bg-bone px-s3 py-s2 space-y-s2"
                data-testid={`energy-audit-activity-${idx}`}
              >
                <div className="flex items-center gap-s2">
                  <input
                    type="time"
                    value={a.startTime}
                    onChange={(e) => setActivity(idx, 'startTime', e.target.value)}
                    className="input-field w-[7.5rem] font-mono text-sm"
                    aria-label={t('tools.energyAudit.fields.start')}
                    data-testid={`energy-audit-start-${idx}`}
                  />
                  <span className="text-gray-1">–</span>
                  <input
                    ref={(el) => {
                      endRefs.current[idx] = el;
                    }}
                    type="time"
                    value={a.endTime}
                    onChange={(e) => setActivity(idx, 'endTime', e.target.value)}
                    className="input-field w-[7.5rem] font-mono text-sm"
                    aria-label={t('tools.energyAudit.fields.end')}
                    data-testid={`energy-audit-end-${idx}`}
                  />
                  <input
                    type="text"
                    value={a.label}
                    onChange={(e) => setActivity(idx, 'label', e.target.value)}
                    placeholder={t('tools.energyAudit.placeholders.label')}
                    className="input-field flex-1"
                    data-testid={`energy-audit-label-${idx}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeActivity(idx)}
                    disabled={form.activities.length <= 1}
                    className="btn-ghost text-sm"
                    aria-label={t('tools.energyAudit.timeline.remove')}
                    data-testid={`energy-audit-remove-${idx}`}
                  >
                    ×
                  </button>
                </div>
                {/* icon · number · slider — the score sits right beside its
                    own axis icon so it can't be misread as the next domain's. */}
                <div className="grid grid-cols-1 gap-x-s4 gap-y-s1 sm:grid-cols-3">
                  {AXES.map((axis) => (
                    <div key={axis} className="flex items-center gap-s2">
                      <AxisIcon axis={axis} size={15} ariaLabel={t(`common.axis.${axis}`)} />
                      <span
                        className="w-4 text-center font-display text-sm font-semibold text-ink"
                        data-testid={`energy-audit-${axis}-val-${idx}`}
                      >
                        {a[axis]}
                      </span>
                      <input
                        type="range"
                        min={1}
                        max={10}
                        step={1}
                        value={a[axis]}
                        onChange={(e) => setActivity(idx, axis, Number.parseInt(e.target.value, 10))}
                        className="h-1 flex-1 accent-flame"
                        aria-label={`${a.label || t('tools.energyAudit.placeholders.label')} — ${t(`common.axis.${axis}`)}`}
                        data-testid={`energy-audit-${axis}-${idx}`}
                      />
                    </div>
                  ))}
                </div>
              </li>
            ))}
          </ol>
          <button
            type="button"
            onClick={addActivity}
            disabled={form.activities.length >= 30}
            className="btn-ghost mt-s3"
            data-testid="energy-audit-add"
          >
            + {t('tools.energyAudit.timeline.add')}
          </button>
        </article>

        {/* Live summary */}
        <article className="rounded-lg border border-flame/40 bg-flame/[0.04] p-s5" data-testid="energy-audit-summary">
          <h2 className="font-display text-display-sm text-ink">
            {t('tools.energyAudit.summary.title')}
          </h2>
          {summary ? (
            <div className="mt-s3 space-y-s3">
              <div className="flex flex-wrap items-center gap-s4">
                <span className="font-mono text-xs uppercase tracking-wide text-gray-1">
                  {t('tools.energyAudit.summary.balance')}
                </span>
                {AXES.map((axis) => (
                  <span key={axis} className="flex items-center gap-s1" data-testid={`energy-audit-summary-${axis}`}>
                    <AxisIcon axis={axis} size={15} ariaLabel={t(`common.axis.${axis}`)} />
                    <span className="font-display text-base text-ink">{summary[axis]}</span>
                  </span>
                ))}
              </div>
              {/* Where each domain peaks — one activity per axis. */}
              <div>
                <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
                  {t('tools.energyAudit.summary.peaks')}
                </p>
                <div className="mt-s1 flex flex-wrap gap-x-s4 gap-y-s1">
                  {AXES.map((axis) => (
                    <span
                      key={axis}
                      className="flex items-center gap-s1 text-sm text-ink"
                      data-testid={`energy-audit-peak-${axis}`}
                    >
                      <AxisIcon axis={axis} size={14} ariaLabel={t(`common.axis.${axis}`)} />
                      <span className="text-semantic-success">▲</span>
                      {summary.peaks[axis].label}
                    </span>
                  ))}
                </div>
              </div>
              <p className="text-sm text-gray-2" data-testid="energy-audit-drain">
                <span className="text-semantic-danger">▼</span>{' '}
                {t('tools.energyAudit.summary.drain', { label: summary.drain.label })}
              </p>
            </div>
          ) : (
            <p className="mt-s2 text-sm text-gray-2">{t('tools.energyAudit.summary.empty')}</p>
          )}
        </article>

        {submitError && (
          <p role="alert" className="form-error" data-testid="energy-audit-submit-error">
            {submitError}
          </p>
        )}

        {savedAt && (
          <p role="status" className="text-sm text-flame-deep" data-testid="energy-audit-saved-at">
            ✓ {t('tools.energyAudit.savedAt', { when: savedAt.slice(0, 19).replace('T', ' ') })}
          </p>
        )}

        <FinalizeWarning />

        <button
          type="submit"
          className="btn-primary w-full"
          disabled={!canSubmit || isSavedClean}
          data-testid="energy-audit-submit"
        >
          {submitting
            ? t('tools.energyAudit.submitting')
            : isSavedClean
              ? t('tools.energyAudit.savedButton')
              : t('tools.energyAudit.submit')}
        </button>
      </form>
    </section>
  );
}

function YesNoToggle({
  label,
  value,
  onChange,
  testId,
  yesLabel,
  noLabel,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  testId: string;
  yesLabel: string;
  noLabel: string;
}): JSX.Element {
  return (
    <div className="flex items-center gap-s2" data-testid={testId}>
      <span className="text-sm text-ink">{label}</span>
      <div className="flex overflow-hidden rounded border border-bone-3" role="group" aria-label={label}>
        {[
          { v: true, txt: yesLabel },
          { v: false, txt: noLabel },
        ].map((opt) => (
          <button
            key={opt.txt}
            type="button"
            onClick={() => onChange(opt.v)}
            aria-pressed={value === opt.v}
            data-testid={`${testId}-${opt.v ? 'yes' : 'no'}`}
            className={`px-s3 py-s1 font-mono text-xs transition-colors ${
              value === opt.v ? 'bg-flame text-bone' : 'bg-bone-2 text-gray-2 hover:text-ink'
            }`}
          >
            {opt.txt}
          </button>
        ))}
      </div>
    </div>
  );
}
